import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../shared/Button';
import { Card } from '../shared/Card';
interface SessionTrackerProps {
  exercises: any[];
  onBack: () => void;
  onComplete: (data: any) => void;
}
export const SessionTracker = ({
  exercises,
  onBack,
  onComplete
}: SessionTrackerProps) => {
  // Split exercises into Part A and Part B
  const partAExercises = exercises.slice(0, 2);
  const partBExercises = exercises.slice(2);
  // Track which part (A or B) we're currently on
  const [currentPart, setCurrentPart] = useState('A');
  // Track the current exercise index within the current part
  const [currentExerciseInPart, setCurrentExerciseInPart] = useState(0);
  // Track the current set across all exercises in a part
  const [currentSet, setCurrentSet] = useState(1);
  // Get the actual current exercise based on part and index
  const currentExerciseList = currentPart === 'A' ? partAExercises : partBExercises;
  const currentExerciseIndex = currentPart === 'A' ? currentExerciseInPart : partAExercises.length + currentExerciseInPart;
  // Track achievement notifications
  const [achievement, setAchievement] = useState(null);
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimerRef = useRef(null);
  const recognitionRef = useRef(null);
  const [sessionData, setSessionData] = useState(exercises.map(ex => ({
    ...ex,
    sets: ex.isCardio ? [{
      completed: false,
      value: 0
    }] : Array(ex.sets).fill({
      completed: false,
      weight: ex.weight,
      reps: ex.reps
    }),
    achievements: [] // Add achievements array to track progress
  })));
  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';
      recognitionRef.current.onresult = event => {
        const transcript = event.results[0][0].transcript.toLowerCase();
        processVoiceInput(transcript);
      };
      recognitionRef.current.onend = () => {
        stopRecording();
      };
      recognitionRef.current.onerror = event => {
        console.error('Speech recognition error', event.error);
        stopRecording();
      };
    }
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);
  // Process voice input based on current exercise type
  const processVoiceInput = transcript => {
    const exercise = sessionData[currentExerciseIndex];
    // Extract numbers from the transcript
    const numberMatch = transcript.match(/\d+/);
    const number = numberMatch ? parseInt(numberMatch[0]) : null;
    if (isCardio(exercise)) {
      // For cardio exercises
      if (number !== null) {
        const cardioInput = document.getElementById('cardio-input') as HTMLInputElement;
        if (cardioInput) {
          cardioInput.value = number.toString();
        }
      }
    } else {
      // For weight training exercises
      const needsWeightInput = needsWeight(exercise.id);
      if (needsWeightInput) {
        // Check for weight-related commands
        if (transcript.includes('stay') || transcript.includes('same')) {
          // "stay the same" command
          const repsInput = document.getElementById('reps-input') as HTMLInputElement;
          updateCurrentSet({
            weight: prevWeight,
            reps: parseInt(repsInput.value) || exercise.reps
          });
          return;
        } else if (transcript.includes('pound') || transcript.includes('lb')) {
          // Weight command like "10 pounds"
          if (number !== null) {
            const repsInput = document.getElementById('reps-input') as HTMLInputElement;
            updateCurrentSet({
              weight: number,
              reps: parseInt(repsInput.value) || exercise.reps
            });
            return;
          }
        } else if (number !== null) {
          // Just a number for reps
          const repsInput = document.getElementById('reps-input') as HTMLInputElement;
          if (repsInput) {
            repsInput.value = number.toString();
          }
        }
      } else {
        // Only reps input needed
        if (number !== null) {
          const repsInput = document.getElementById('reps-input') as HTMLInputElement;
          if (repsInput) {
            repsInput.value = number.toString();
          }
        }
      }
    }
  };
  // Start voice recording
  const startRecording = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
        setIsRecording(true);
        setRecordingTime(0);
        // Start timer to show recording duration
        recordingTimerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
          // Auto-stop after 10 seconds if no result
          if (recordingTime >= 10) {
            stopRecording();
          }
        }, 1000);
      } catch (error) {
        console.error('Error starting speech recognition:', error);
      }
    }
  };
  // Stop voice recording
  const stopRecording = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error('Error stopping speech recognition:', error);
      }
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
    setRecordingTime(0);
  };
  // Check if an exercise needs weight input
  const needsWeight = exerciseId => {
    const noWeightExercises = ['plank', 'treadmill', 'bike', 'elliptical', 'rower'];
    return !noWeightExercises.includes(exerciseId);
  };
  // Check if an exercise is cardio
  const isCardio = exercise => {
    return exercise.isCardio;
  };
  // Calculate weight per side (total weight minus bar weight, divided by 2)
  const calculateWeightPerSide = (totalWeight, barWeight) => {
    const weightPerSide = (totalWeight - barWeight) / 2;
    return weightPerSide > 0 ? weightPerSide : 0;
  };
  // Check if current performance is a PR or improvement
  const checkForAchievements = (exercise, currentData) => {
    const achievements = [];
    // Skip for cardio exercises
    if (isCardio(exercise)) {
      if (exercise.lastSession && exercise.lastSession.value) {
        const previousValue = exercise.lastSession.value;
        const currentValue = currentData.value;
        if (currentValue > previousValue) {
          const increase = currentValue - previousValue;
          const percentIncrease = Math.round(increase / previousValue * 100);
          if (percentIncrease >= 10) {
            achievements.push(`${percentIncrease}% increase!`);
          } else {
            achievements.push(`+${increase} ${exercise.trackingType === 'calories' ? 'cal' : 'min'}!`);
          }
        }
      }
      return achievements;
    }
    // For weighted exercises
    if (exercise.lastSession && needsWeight(exercise.id)) {
      const previousWeight = exercise.lastSession.weight;
      const currentWeight = currentData.weight;
      // Check for weight increases
      if (currentWeight > previousWeight) {
        const increase = currentWeight - previousWeight;
        const percentIncrease = Math.round(increase / previousWeight * 100);
        if (percentIncrease >= 5) {
          achievements.push(`${percentIncrease}% increase!`);
        } else {
          achievements.push(`+${increase}lb!`);
        }
        // If this is a significant increase (10%+), it's a new PR
        if (percentIncrease >= 10) {
          achievements.push('New PR!');
        }
      }
      // Check for rep increases at same weight
      if (currentWeight === previousWeight && currentData.reps > exercise.lastSession.reps) {
        const repIncrease = currentData.reps - exercise.lastSession.reps;
        achievements.push(`+${repIncrease} reps!`);
      }
    }
    return achievements;
  };
  const updateCurrentSet = data => {
    const newSessionData = [...sessionData];
    const currentExerciseData = newSessionData[currentExerciseIndex];
    // Prepare the set data
    let setData = {};
    if (isCardio(currentExerciseData)) {
      setData = {
        completed: true,
        value: data.value || 0
      };
      newSessionData[currentExerciseIndex].sets[0] = setData;
    } else {
      setData = {
        completed: true,
        weight: needsWeight(currentExerciseData.id) ? data.weight : 0,
        reps: data.reps
      };
      newSessionData[currentExerciseIndex].sets[currentSet - 1] = setData;
    }
    // Check for achievements
    const achievements = checkForAchievements(currentExerciseData, setData);
    // If we have achievements, store them and show notification
    if (achievements.length > 0) {
      newSessionData[currentExerciseIndex].achievements = [...achievements, ...(newSessionData[currentExerciseIndex].achievements || [])];
      // Show achievement notification
      setAchievement({
        exerciseName: currentExerciseData.name,
        messages: achievements
      });
      // Clear achievement after 3 seconds
      setTimeout(() => {
        setAchievement(null);
      }, 3000);
    }
    setSessionData(newSessionData);
    // Determine the next exercise/set
    const maxExercisesInPart = currentPart === 'A' ? partAExercises.length : partBExercises.length;
    const maxSets = currentExerciseData.sets?.length || 3;
    // If we've reached the end of exercises in this part for the current set
    if (currentExerciseInPart >= maxExercisesInPart - 1) {
      // If we've completed all sets for this part
      if (currentSet >= maxSets) {
        // If we're in Part A, move to Part B
        if (currentPart === 'A') {
          setCurrentPart('B');
          setCurrentExerciseInPart(0);
          setCurrentSet(1);
        } else {
          // Workout complete
          onComplete({
            exercises: sessionData,
            totalSets: sessionData.reduce((acc, ex) => acc + (ex.isCardio ? 1 : ex.sets.length), 0),
            totalReps: sessionData.reduce((acc, ex) => acc + (ex.isCardio ? 0 : ex.sets.reduce((setAcc, set) => setAcc + (set.completed ? set.reps : 0), 0)), 0),
            totalVolume: sessionData.reduce((acc, ex) => acc + (ex.isCardio ? 0 : ex.sets.reduce((setAcc, set) => setAcc + (set.completed ? set.weight * set.reps : 0), 0)), 0),
            totalCardio: sessionData.reduce((acc, ex) => acc + (ex.isCardio && ex.sets[0].completed ? ex.sets[0].value : 0), 0),
            date: new Date(),
            achievements: sessionData.reduce((acc, ex) => {
              if (ex.achievements && ex.achievements.length) {
                return [...acc, {
                  name: ex.name,
                  achievements: ex.achievements
                }];
              }
              return acc;
            }, [])
          });
        }
      } else {
        // Move to the next set, first exercise in the part
        setCurrentExerciseInPart(0);
        setCurrentSet(currentSet + 1);
      }
    } else {
      // Move to the next exercise in the current part, same set
      setCurrentExerciseInPart(currentExerciseInPart + 1);
    }
  };
  const exercise = sessionData[currentExerciseIndex];
  const prevWeight = !isCardio(exercise) && currentSet > 1 ? exercise.sets[currentSet - 2].weight : exercise.weight;
  const showWeightControls = needsWeight(exercise.id) && !isCardio(exercise);
  const formatDate = dateString => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Check if a set is completed
  const isSetCompleted = (exerciseIndex, setIndex) => {
    const ex = sessionData[exerciseIndex];
    if (isCardio(ex)) {
      return ex.sets[0].completed;
    }
    return ex.sets[setIndex] && ex.sets[setIndex].completed;
  };
  // Check if this is a barbell exercise
  const isBarbellExercise = () => {
    return exercise.equipment === 'barbell' && exercise.barWeight;
  };
  // Get the current part label
  const getCurrentPartLabel = () => {
    return `Part ${currentPart}: Set ${currentSet} of ${exercise.sets?.length || 3}`;
  };
  return <div className="flex flex-col h-full" data-id="element-205">
      <div className="p-6" data-id="element-206">
        <button onClick={onBack} className="text-gray-500 mb-4" data-id="element-207">
          ← Back
        </button>
        <h1 className="text-xl font-bold text-gray-900 mb-1" data-id="element-208">
          {exercise.name}
        </h1>
        {exercise.equipment && <p className="text-gray-600 text-sm" data-id="element-209">
            {exercise.equipment.charAt(0).toUpperCase() + exercise.equipment.slice(1)}
            {exercise.equipment === 'barbell' && exercise.barWeight && ` (${exercise.barWeight}lb bar)`}
          </p>}

        {/* Part and Set indicator */}
        <div className="mt-2 mb-4" data-id="element-210">
          <div className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium" data-id="element-211">
            {getCurrentPartLabel()}
          </div>
        </div>

        {/* Set progress indicators */}
        {!isCardio(exercise) && <div className="flex flex-col space-y-3 mt-4 mb-2" data-id="element-212">
            <p className="text-sm text-gray-600 font-medium" data-id="element-213">Progress:</p>
            {currentPart === 'A' && <div className="space-y-2" data-id="element-214">
                {partAExercises.map((ex, exIndex) => <div key={`A-${exIndex}`} className="flex items-center" data-id="element-215">
                    <span className="text-sm text-gray-600 w-28 truncate" data-id="element-216">
                      {ex.name}:
                    </span>
                    <div className="flex space-x-2" data-id="element-217">
                      {Array.from({
                length: ex.sets || 3
              }).map((_, setIndex) => <div key={`A-${exIndex}-${setIndex}`} className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium 
                            ${isSetCompleted(exIndex, setIndex) ? 'bg-green-500 text-white' : currentPart === 'A' && currentExerciseInPart === exIndex && setIndex + 1 === currentSet ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`} data-id="element-218">
                          {isSetCompleted(exIndex, setIndex) ? <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-219">
                              <path d="M20 6 9 17l-5-5" data-id="element-220"></path>
                            </svg> : setIndex + 1}
                        </div>)}
                    </div>
                  </div>)}
              </div>}
            {currentPart === 'B' && <div className="space-y-2" data-id="element-221">
                {partBExercises.map((ex, exIndex) => <div key={`B-${exIndex}`} className="flex items-center" data-id="element-222">
                    <span className="text-sm text-gray-600 w-28 truncate" data-id="element-223">
                      {ex.name}:
                    </span>
                    <div className="flex space-x-2" data-id="element-224">
                      {Array.from({
                length: ex.sets || 3
              }).map((_, setIndex) => <div key={`B-${exIndex}-${setIndex}`} className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium 
                            ${isSetCompleted(partAExercises.length + exIndex, setIndex) ? 'bg-green-500 text-white' : currentPart === 'B' && currentExerciseInPart === exIndex && setIndex + 1 === currentSet ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`} data-id="element-225">
                          {isSetCompleted(partAExercises.length + exIndex, setIndex) ? <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-226">
                              <path d="M20 6 9 17l-5-5" data-id="element-227"></path>
                            </svg> : setIndex + 1}
                        </div>)}
                    </div>
                  </div>)}
              </div>}
          </div>}

        {isCardio(exercise) && <p className="text-blue-500 font-medium mt-3" data-id="element-228">
            Cardio Session -{' '}
            {exercise.trackingType === 'calories' ? 'Calories' : 'Time'}
          </p>}

        {/* Achievement notification */}
        {achievement && <div className="mt-3 mb-2 p-3 bg-green-100 border border-green-300 rounded-xl animate-pulse" data-id="element-229">
            <div className="flex items-center" data-id="element-230">
              <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center mr-2 text-green-600" data-id="element-231">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-232">
                  <path d="M8 10V20M8 10L4 9.99998V20L8 20M8 10L13.1956 3.93847C13.6886 3.3633 14.4642 3.11604 15.1992 3.29977L15.2467 3.31166C16.5885 3.64711 17.1929 5.21057 16.4258 6.36135L14 9.99998H18.5604C19.8225 9.99998 20.7691 11.1546 20.5216 12.3922L19.3216 18.3922C19.1346 19.3271 18.3138 20 17.3604 20L8 20" data-id="element-233"></path>
                </svg>
              </div>
              <div data-id="element-234">
                <p className="font-medium text-green-800" data-id="element-235">
                  {achievement.exerciseName}: {achievement.messages.join(' ')}
                </p>
              </div>
            </div>
          </div>}

        {/* Current weight in large format */}
        {showWeightControls && <div className="mt-5 mb-4 p-5 bg-blue-50 rounded-xl text-center" data-id="element-236">
            <p className="text-sm text-blue-700 mb-1" data-id="element-237">Current Weight</p>
            <div className="text-4xl font-bold text-blue-800" data-id="element-238">
              {prevWeight} <span className="text-2xl" data-id="element-239">lb</span>
            </div>
            {/* Weight breakdown for barbell exercises */}
            {isBarbellExercise() && <p className="text-sm text-blue-700 mt-2" data-id="element-240">
                {exercise.barWeight}lb bar +{' '}
                {calculateWeightPerSide(prevWeight, exercise.barWeight)}lb per
                side
              </p>}
          </div>}

        {showWeightControls && <div className="mt-4 bg-gray-100 rounded-xl p-4" data-id="element-241">
            <div className="flex justify-between items-center" data-id="element-242">
              <span className="text-gray-600" data-id="element-243">Target reps:</span>
              <span className="font-medium" data-id="element-244">{exercise.reps}</span>
            </div>
          </div>}

        {!showWeightControls && !isCardio(exercise) && <div className="mt-6 bg-gray-100 rounded-xl p-4" data-id="element-245">
            <div className="flex justify-between items-center" data-id="element-246">
              <span className="text-gray-600" data-id="element-247">Target reps:</span>
              <span className="font-medium" data-id="element-248">{exercise.reps}</span>
            </div>
          </div>}

        {isCardio(exercise) && <div className="mt-6 bg-gray-100 rounded-xl p-4" data-id="element-249">
            <div className="flex justify-between items-center" data-id="element-250">
              <span className="text-gray-600" data-id="element-251">
                Target{' '}
                {exercise.trackingType === 'calories' ? 'calories' : 'minutes'}:
              </span>
              <span className="font-medium" data-id="element-252">
                {exercise.target}{' '}
                {exercise.trackingType === 'calories' ? 'cal' : 'min'}
              </span>
            </div>
          </div>}

        {exercise.lastSession && <div className="mt-3 bg-blue-50 p-3 rounded-xl" data-id="element-253">
            <div className="flex justify-between items-center" data-id="element-254">
              <span className="text-gray-600 text-sm" data-id="element-255">Last session:</span>
              <span className="font-medium text-sm text-blue-700" data-id="element-256">
                {exercise.lastSession.weight}lb on{' '}
                {formatDate(exercise.lastSession.date)}
              </span>
            </div>
            {exercise.lastSession.equipment && <div className="text-xs text-gray-500 mt-1" data-id="element-257">
                Using {exercise.lastSession.equipment}
                {exercise.lastSession.barWeight && ` (${exercise.lastSession.barWeight}lb bar)`}
              </div>}
            {exercise.lastSession.equipment === 'barbell' && exercise.lastSession.barWeight && <div className="text-xs text-blue-700 mt-1" data-id="element-258">
                  {exercise.lastSession.barWeight}lb bar +{' '}
                  {calculateWeightPerSide(exercise.lastSession.weight, exercise.lastSession.barWeight)}
                  lb per side
                </div>}
          </div>}
      </div>

      <div className="flex-1 px-6 pb-6" data-id="element-259">
        {!isCardio(exercise) && <>
            <h2 className="font-medium mb-3" data-id="element-260">
              How many reps did you complete?
            </h2>
            <input type="number" defaultValue={exercise.reps} className="w-full p-4 text-center text-xl border border-gray-300 rounded-xl mb-6" id="reps-input" data-id="element-261" />
          </>}

        {isCardio(exercise) && <>
            <h2 className="font-medium mb-3" data-id="element-262">
              {exercise.trackingType === 'calories' ? 'How many calories did you burn?' : 'How many minutes did you exercise?'}
            </h2>
            <div className="relative mb-6" data-id="element-263">
              <input type="number" defaultValue={exercise.target} className="w-full p-4 text-center text-xl border border-gray-300 rounded-xl pr-12" step={exercise.trackingType === 'calories' ? 50 : 5} id="cardio-input" data-id="element-264" />
              <span className="absolute right-4 top-4 text-gray-500" data-id="element-265">
                {exercise.trackingType === 'calories' ? 'cal' : 'min'}
              </span>
            </div>
          </>}

        {showWeightControls && <>
            <h2 className="font-medium mb-3" data-id="element-266">Weight for next set:</h2>
            <div className="grid grid-cols-3 gap-3 mb-4" data-id="element-267">
              <Button onClick={() => {
            const repsInput = document.getElementById('reps-input') as HTMLInputElement;
            updateCurrentSet({
              weight: prevWeight,
              reps: parseInt(repsInput.value) || exercise.reps
            });
          }} variant="secondary" className="py-3" data-id="element-268">
                Stay same
              </Button>
              <Button onClick={() => {
            const repsInput = document.getElementById('reps-input') as HTMLInputElement;
            updateCurrentSet({
              weight: prevWeight + 5,
              reps: parseInt(repsInput.value) || exercise.reps
            });
          }} variant="secondary" className="py-3" data-id="element-269">
                +5 lb
              </Button>
              <Button onClick={() => {
            const repsInput = document.getElementById('reps-input') as HTMLInputElement;
            updateCurrentSet({
              weight: prevWeight - 5,
              reps: parseInt(repsInput.value) || exercise.reps
            });
          }} variant="secondary" className="py-3" data-id="element-270">
                -5 lb
              </Button>
            </div>
            {/* Weight per side helper for barbell exercises */}
            {isBarbellExercise() && <div className="mb-6 p-3 bg-gray-50 rounded-lg" data-id="element-271">
                <p className="text-sm text-gray-600 mb-2" data-id="element-272">Weight per side:</p>
                <div className="grid grid-cols-3 gap-2 text-center" data-id="element-273">
                  <div className="p-2 bg-gray-100 rounded" data-id="element-274">
                    <p className="font-medium" data-id="element-275">
                      {calculateWeightPerSide(prevWeight, exercise.barWeight)}lb
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-276">Current</p>
                  </div>
                  <div className="p-2 bg-gray-100 rounded" data-id="element-277">
                    <p className="font-medium" data-id="element-278">
                      {calculateWeightPerSide(prevWeight + 5, exercise.barWeight)}
                      lb
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-279">+5lb</p>
                  </div>
                  <div className="p-2 bg-gray-100 rounded" data-id="element-280">
                    <p className="font-medium" data-id="element-281">
                      {calculateWeightPerSide(prevWeight - 5, exercise.barWeight)}
                      lb
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-282">-5lb</p>
                  </div>
                </div>
              </div>}
          </>}

        <div className="bg-blue-50 rounded-xl p-4 flex items-start" data-id="element-283">
          <button onClick={isRecording ? stopRecording : startRecording} className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0 transition-colors ${isRecording ? 'bg-red-500 text-white animate-pulse' : 'bg-blue-100 text-blue-500'}`} data-id="element-284">
            {isRecording ? <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-285">
                <rect x="6" y="6" width="12" height="12" rx="2" data-id="element-286"></rect>
              </svg> : <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-287">
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" data-id="element-288"></path>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" data-id="element-289"></path>
                <line x1="12" x2="12" y1="19" y2="22" data-id="element-290"></line>
              </svg>}
          </button>
          <div data-id="element-291">
            <p className="text-sm text-blue-700" data-id="element-292">
              {isRecording ? <span className="font-medium" data-id="element-293">
                  Listening... {recordingTime}s
                </span> : <span data-id="element-294">
                  Voice enabled!{' '}
                  {isCardio(exercise) ? `Say the number of ${exercise.trackingType === 'calories' ? 'calories' : 'minutes'}` : showWeightControls ? '"stay" or give a number (e.g., "10 pounds")' : 'the number of reps you completed'}
                </span>}
            </p>
            {isRecording && <p className="text-xs text-blue-600 mt-1" data-id="element-295">
                Speak clearly, tap to stop
              </p>}
          </div>
        </div>
      </div>

      <div className="p-4 border-t border-gray-100" data-id="element-296">
        <Button onClick={() => {
        if (isCardio(exercise)) {
          const cardioInput = document.getElementById('cardio-input') as HTMLInputElement;
          updateCurrentSet({
            value: parseInt(cardioInput.value) || exercise.target
          });
        } else if (!showWeightControls) {
          const repsInput = document.getElementById('reps-input') as HTMLInputElement;
          updateCurrentSet({
            reps: parseInt(repsInput.value) || exercise.reps
          });
        }
      }} variant="primary" fullWidth className="py-3 text-lg" data-id="element-297">
          {isCardio(exercise) ? 'Complete Cardio Session' : 'Next Exercise'}
        </Button>
      </div>
    </div>;
};